import type { CartItemInputDto, CartItemResponseDto, CartOutputDto } from "@sticker-giant/sg-frontend-common";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CART_ACTIVE_FILTER, CART_QUERY_KEY, commitCart } from "@/queries/useCart";
import { useServicesStore } from "@/stores/useServicesStore";
import { getRollDriver } from "@/utils/rollQuantityUtils";

export const updateItemQuantityMutationKey = ["updateItemQuantity"] as const;

export function useUpdateItemQuantity() {
  const cartClient = useServicesStore(state => state.cartClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: updateItemQuantityMutationKey,
    mutationFn: async (item: CartItemInputDto) => {
      if (!cartClient) throw new Error("Cart client is not available.");
      return await cartClient.updateCartItem(item);
    },
    onMutate: async (item: CartItemInputDto) => {
      await queryClient.cancelQueries({ queryKey: CART_QUERY_KEY });

      const snapshots = queryClient.getQueriesData<CartOutputDto | null>(CART_ACTIVE_FILTER);

      queryClient.setQueriesData<CartOutputDto | null>(
        CART_ACTIVE_FILTER,
        (current: CartOutputDto | null | undefined) => {
          if (!current) return current;
          return {
            ...current,
            items: current.items.map(cartItem =>
              cartItem.id === item.id
                ? {
                    ...cartItem,
                    quantity: item.quantity,
                    rollCount: item.rollCount,
                    rollQty: item.rollQty,
                    rollDriver: getRollDriver(item),
                  }
                : cartItem,
            ),
          };
        },
      );

      return { snapshots };
    },
    onSuccess: (response?: CartItemResponseDto) => {
      if (!response) return;
      commitCart(queryClient, response.cart);
    },
    onError: (_error, _item, context) => {
      context?.snapshots.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
  });
}
